<template>
  <div class="flex flex-col gap-2">
    <InteractionItemHeader
      :mate="event.mate"
      :title="event.title"
      :desc="desc"
      icon="iconify mingcute--flag-1-fill"
      :dtCreate="event.dtCreate"
      :is-need-action="isNeedAction"
    />

    <div :class="[isNeedAction ? 'text-primary-content' : 'text-base-content']">
      {{ event.content }}
    </div>

    <div
      v-if="event.reviewStatus === InteractionEventStatus.Approved"
      class="text-success"
    >
      {{ approveText }}
    </div>
    <div
      v-else-if="event.reviewStatus === InteractionEventStatus.Rejected"
      class="text-error"
    >
      {{ rejectText }}
    </div>

    <template v-else-if="event.reviewStatus === InteractionEventStatus.Pending">
      <div class="flex gap-2">
        <button
          :class="[
            'btn btn-xs rounded',
            isNeedAction
              ? 'text-primary-content border-primary-content bg-transparent'
              : 'btn-outline',
          ]"
          @click="clickDetail(false)"
        >
          {{ t("interactive.reviewPage.goToView") }}
        </button>
        <button
          :class="[
            'btn btn-xs rounded',
            isNeedAction
              ? 'text-primary-content border-primary-content bg-transparent'
              : 'btn-outline',
          ]"
          @click="
            reviewStore.reviewEvent(
              props.event.id,
              InteractionEventStatus.Approved,
              isFree ? 0 : dispatch.costPowerCubes
            )
          "
        >
          {{ t("Buttons.agree") }}
          <template v-if="isFree">
            <span class="text-success/40">(领队已为您买单)</span>
          </template>
          <template v-else>
            <PowerIcon />
            {{ userStore.getPowerCostByModel(dispatch.costPowerCubes || 0) }}
          </template>
        </button>

        <button
          :class="[
            'btn btn-xs rounded',
            isNeedAction
              ? 'text-primary-content border-primary-content bg-transparent'
              : 'btn-outline',
          ]"
          @click="clickDetail(true)"
        >
          {{ t("game.modify") }}
        </button>
        <button
          :class="[
            'btn btn-xs rounded',
            isNeedAction
              ? 'text-primary-content border-primary-content bg-transparent'
              : 'btn-outline',
          ]"
          @click="
            reviewStore.reviewEvent(
              props.event.id,
              InteractionEventStatus.Rejected
            )
          "
        >
          {{ t("Buttons.deny") }}
        </button>
      </div>
    </template>
    <template v-else-if="event.type === InteractionEventType.MissionReturned">
      <button
        v-if="isMyMateWaitSettled"
        class="btn btn-xs rounded btn-outline w-fit"
        @click="clickSettle"
      >
        {{ t("mission.statusBtnMap.3") }}
      </button>
      <button
        v-else
        class="btn btn-xs rounded btn-outline w-fit"
        @click="clickToJourney"
      >
        {{ t("interactive.reviewPage.goToView") }}
      </button>
    </template>

    <button
      v-else-if="event.type === InteractionEventType.DispatchedMission"
      class="btn btn-xs rounded btn-outline w-fit"
      @click="clickDetail(false)"
    >
      {{ t("interactive.reviewPage.goToView") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { InteractionEventStatus, InteractionEventType } from "@/enums/mission";
import { InteractionEvent, MateDispatch, MateInvitation } from "@/types/entity";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useMate } from "@/stores/mate";
import { useMission } from "@/stores/mission";
import { showLoadingToast } from "vant";
import { DispatchMemberPayerType, SettlementStatus } from "@/enums/mateEnums";
import { useInteractiveReview } from "@/stores/interactive-review";
import { useGlobalUser } from "@/stores/globalUser";
const props = defineProps<{
  event: InteractionEvent;
  isNeedAction: boolean;
}>();

const missionStore = useMission();
const reviewStore = useInteractiveReview();
const mateStore = useMate();
const router = useRouter();
const { t } = useI18n();
const userStore = useGlobalUser();

const entity = computed<MateInvitation | MateDispatch>(() => {
  return props.event.entity as MateInvitation | MateDispatch;
});

const dispatch = computed<MateDispatch>(() => {
  return (
    ((entity.value as MateInvitation).entity as MateDispatch) ||
    (entity.value as MateDispatch)
  );
});

const isMyAction = computed<boolean>(() => {
  return mateStore.isMyMateById((entity.value as MateInvitation).senderId);
});

const getMyMateNameAndTargetName = () => {
  let targetName = "";

  if (props.event.type === InteractionEventType.RequestMission) {
    const mateDispatch = entity.value as MateDispatch;
    const otherMembers =
      mateDispatch.dispatchMemberzz?.filter(
        (v) => !mateStore.isMyMateById(v.mateId)
      ) || [];
    targetName = otherMembers.map((v) => v.mate?.name).join("、");
  }

  const mateInvitation = entity.value as MateInvitation;
  if (!targetName) {
    targetName = isMyAction.value
      ? mateInvitation.recipient?.name || ""
      : mateInvitation.sender?.name || "";
  }

  return {
    name: props.event.mate?.name,
    targetName,
  };
};

const rejectText = computed<string>(() => {
  return t(
    isMyAction.value
      ? `interactive.reviewPage.mission.userRejectedInvitation`
      : `interactive.reviewPage.mission.userRejected`,
    getMyMateNameAndTargetName()
  );
});

const approveText = computed<string>(() => {
  return t(
    isMyAction.value
      ? `interactive.reviewPage.mission.userApprovedInvitation`
      : `interactive.reviewPage.mission.userApproved`,
    getMyMateNameAndTargetName()
  );
});

const desc = computed<string>(() => {
  if (props.event.type === InteractionEventType.ReceivedMissionInvitation) {
    return isMyAction.value ? "任务邀请已发送" : "收到了任务邀请";
  } else if (
    props.event.type === InteractionEventType.MissionInvitationApproved
  ) {
    return isMyAction.value ? "任务邀请被接受" : "接受了任务邀请";
  } else if (
    props.event.type === InteractionEventType.MissionInvitationRejected
  ) {
    return isMyAction.value ? "任务邀请被拒绝" : "拒绝了任务邀请";
  } else if (props.event.type === InteractionEventType.MissionReturned) {
    return "回来了";
  } else if (props.event.type === InteractionEventType.DispatchedMission) {
    return "执行任务";
  }
  return "";
});

const isMyMateWaitSettled = computed<boolean>(() => {
  if (dispatch.value) {
    const currentMate = dispatch.value.dispatchMemberzz?.find(
      (v) => v.mateId === props.event.mateId
    );
    return currentMate?.settlementStatus === SettlementStatus.Pending;
  }
  return false;
});

const isFree = computed<boolean>(() => {
  if (dispatch.value) {
    const activeMember = dispatch.value.dispatchMemberzz?.find(
      (v) => v.mateId === props.event.mateId
    );

    if (activeMember?.payerType === DispatchMemberPayerType.Host) {
      return true;
    }
  }
  return false;
});

const clickToJourney = () => {
  const dispatch = props.event.entity as MateDispatch;
  router.push({
    name: "missionJourneyItem",
    query: {
      dispatchUuid: dispatch.uuid,
      mateId: props.event.mateId,
    },
  });
};
const clickSettle = async () => {
  const dispatch = props.event.entity as MateDispatch;
  const loading = showLoadingToast(t(`Message.loading`));
  try {
    await missionStore.settleMateDispatch(dispatch.id, props.event.mateId);
    clickToJourney();
    loading.close();
  } catch {
    loading.close();
  }
};

const clickDetail = (isEdit: boolean = false) => {
  if (
    props.event.type === InteractionEventType.DispatchedMission ||
    props.event.type === InteractionEventType.RequestMission
  ) {
    router.push({
      name: "missionDetail",
      query: {
        dispatchUuid: dispatch.value.uuid,
        mateId: props.event.mateId,
        isEdit: isEdit ? 1 : undefined,
      },
    });
  } else {
    router.push({
      name: "missionDetail",
      query: {
        dispatchUuid: dispatch.value.uuid,
        invitationId: props.event.entityId,
        mateId: props.event.mateId,
        eventId: props.event.id,
      },
    });
  }
};
</script>
